import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag } from '@fortawesome/pro-light-svg-icons/faTag';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../../common/types';
import { useTRPCAPI, useTRPCQuery } from '../../../../trpc/trpc';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { useSMLGenreParam } from '../../hooks/useSMLGenreParam';
import { useShareTokenContext } from '../../contexts/ShareTokenContext';
import { genreParamName } from './consts';
import styles from './SMLGenreSelector.module.scss';

const SMLGenreSelectorComponent: React.FC = () => {
  const intl = useIntl();
  const smlRouter = useSMLRouter();
  const { genre } = useSMLGenreParam();
  const { isPublicMode } = useShareTokenContext();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const trpcMusicAPI = useTRPCAPI();
  const { data: genresData } = useTRPCQuery(trpcMusicAPI.genres.list.queryOptions());

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointer = (event: MouseEvent) => {
      if (ref.current !== null && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const options = useMemo(() => genresData?.genres ?? [], [genresData?.genres]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') {
      return options;
    }
    return options.filter((g) => g.toLowerCase().includes(q));
  }, [options, query]);

  const setGenre = useCallback(
    (value: string | undefined) => {
      smlRouter.setParams({ [genreParamName]: value } as never);
    },
    [smlRouter]
  );

  const handleSelect = useCallback(
    (value: string) => {
      // Single-select: re-picking the active genre clears back to all genres.
      setGenre(value === genre ? undefined : value);
      setQuery('');
      setOpen(false);
    },
    [genre, setGenre]
  );

  const handleClear = useCallback(() => {
    setGenre(undefined);
    setQuery('');
    setOpen(false);
  }, [setGenre]);

  const buttonLabel =
    genre !== undefined
      ? genre
      : intl.formatMessage({ id: 'app.sml.genreSelector.allGenres', defaultMessage: 'All genres' });

  return (
    <div className={`${styles.geo} ${open ? styles.open : ''}`} ref={ref}>
      <button
        type="button"
        className={`${styles.geoBtn} ${genre !== undefined ? styles.on : ''}`}
        onClick={() => setOpen((value) => !value)}
        disabled={isPublicMode}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={intl.formatMessage({ id: 'app.sml.genreSelector.label', defaultMessage: 'Filter by genre' })}
      >
        <FontAwesomeIcon icon={faTag} className={styles.geoTagIcon} />
        <span className={styles.geoLbl}>{buttonLabel}</span>
        <FontAwesomeIcon icon={faChevronRight} className={styles.cx} />
      </button>
      {open && !isPublicMode ? (
        <div
          className={styles.geoPop}
          role="dialog"
          aria-label={intl.formatMessage({ id: 'app.sml.genreSelector.label', defaultMessage: 'Filter by genre' })}
        >
          <div className={styles.geoSearch}>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={intl.formatMessage({
                id: 'app.sml.genreSelector.searchPlaceholder',
                defaultMessage: 'Filter genres…',
              })}
            />
          </div>
          <div className={styles.geoList}>
            {filtered.map((g) => {
              const isActive = g === genre;
              return (
                <button
                  type="button"
                  key={g}
                  className={`${styles.geoItem} ${isActive ? styles.on : ''}`}
                  onClick={() => handleSelect(g)}
                >
                  <span className={styles.geoCheck}>{isActive ? <FontAwesomeIcon icon={faCheck} /> : null}</span>
                  <span className={styles.geoCn}>{g}</span>
                </button>
              );
            })}
            {filtered.length === 0 ? (
              <div className={styles.geoEmpty}>
                {intl.formatMessage(
                  { id: 'app.sml.genreSelector.empty', defaultMessage: 'No genre matches "{query}".' },
                  { query }
                )}
              </div>
            ) : null}
          </div>
          <div className={styles.geoFoot}>
            <button type="button" className={styles.geoClear} onClick={handleClear} disabled={genre === undefined}>
              {intl.formatMessage({ id: 'app.sml.genreSelector.clear', defaultMessage: 'Clear' })}
            </button>
            <button type="button" className={styles.geoDone} onClick={() => setOpen(false)}>
              {intl.formatMessage({ id: 'app.sml.genreSelector.done', defaultMessage: 'Done' })}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const SMLGenreSelector = TypedMemo(SMLGenreSelectorComponent);
